import { useMemo, useState } from 'react';

import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  Play,
  Save,
  Wand2,
} from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Segmented } from '@/components/feedback/Segmented';
import { EditList, PromptDiff } from '@/components/PromptDiff';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useClassifierConfigList } from '@/hooks/useClassifierConfigs';
import {
  useApplyJudgeTuningRun,
  useCreateJudgeTuningRun,
  useJudgeTuningRunDetail,
  useRunJudgeTuningRun,
  useUpdateJudgeTuningRun,
} from '@/hooks/useJudgeTuning';
import { useMisclassifiedCallList } from '@/hooks/useMisclassifiedCalls';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useQualityConfigList } from '@/hooks/useQualityConfigs';
import { parseCallIdsParam } from '@/lib/parse-call-ids';
import { cn } from '@/lib/utils';

import type {
  JudgeTuningConfigResult,
  JudgeTuningConfigStatus,
  JudgeTuningIteration,
  JudgeTuningIterationResults,
  JudgeTuningPromptEdit,
  JudgeTuningStatus,
} from '@/lib/api/judge-tuning.api';
import type { FeedbackTargetType, MisclassifiedCall } from '@/lib/api/misclassified-calls.api';

type RowState = 'auto' | 'include' | 'exclude';
type PromptView = 'changes' | 'full';

const pct = (rate: number | undefined) => (rate === undefined ? '—' : `${Math.round(rate * 100)}%`);

const STATUS_COLOR: Record<JudgeTuningStatus | JudgeTuningConfigStatus, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  QUEUED: 'bg-blue-100 text-blue-700',
  RUNNING: 'bg-blue-100 text-blue-700',
  SUCCEEDED: 'bg-emerald-100 text-emerald-800',
  PARTIAL: 'bg-amber-100 text-amber-800',
  FAILED: 'bg-rose-100 text-rose-800',
  CANCELLED: 'bg-slate-100 text-slate-600',
  SKIPPED: 'bg-slate-100 text-slate-600',
};

const STAGE_LABEL: Record<string, string> = {
  collecting: 'Collecting samples',
  baseline: 'Running baseline',
  optimizing: 'Optimizing prompt',
  evaluating: 'Evaluating candidate',
  finalizing: 'Finalizing',
  done: 'Done',
  failed: 'Failed',
};

const StatusPill = ({ status }: { status: JudgeTuningStatus | JudgeTuningConfigStatus }) => (
  <span
    className={cn(
      'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
      STATUS_COLOR[status],
    )}
  >
    {status}
  </span>
);

/** Extract edits from an iteration's results (structured format). */
const extractEdits = (
  results: JudgeTuningIterationResults,
): { edits?: JudgeTuningPromptEdit[]; append?: string } | undefined => {
  if (Array.isArray(results)) return undefined;
  if (!results.edits && !results.append) return undefined;
  return {
    edits: results.edits,
    append: results.append,
  };
};

/** Count edits in an iteration's structured results. */
const editCount = (results: JudgeTuningIterationResults): number => {
  if (Array.isArray(results)) return 0;
  return (results.edits?.length ?? 0) + (results.append ? 1 : 0);
};

/** Format a relative time string from an ISO timestamp. */
const timeAgo = (iso: string): string => {
  const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

export const JudgeTuningEditPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id: routeId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isNew = !routeId || routeId === 'new';

  // Parse ?calls= param (comma-separated UUIDs from the run page)
  const preselectedCallIds = useMemo(
    () => (isNew ? parseCallIdsParam(searchParams.get('calls')) : []),
    [isNew, searchParams],
  );

  usePageTitle(isNew ? 'New Judge Tuning Run' : 'Judge Tuning Run');

  const { data: detail, isLoading } = useJudgeTuningRunDetail(isNew ? undefined : routeId);
  const { data: classifierConfigs } = useClassifierConfigList();
  const { data: qualityConfigs } = useQualityConfigList();
  const createMutation = useCreateJudgeTuningRun();
  const updateMutation = useUpdateJudgeTuningRun();
  const runMutation = useRunJudgeTuningRun();
  const applyMutation = useApplyJudgeTuningRun();

  const [name, setName] = useState('');
  const [targetType, setTargetType] = useState<FeedbackTargetType>('classifier');
  const [targetConfigIds, setTargetConfigIds] = useState<string[]>([]);
  const [targetAllUsed, setTargetAllUsed] = useState(false);
  const [optimizerModel, setOptimizerModel] = useState('gemini-2.5-pro');
  const [maxIterations, setMaxIterations] = useState('3');
  const [autoUpdate, setAutoUpdate] = useState(false);
  const [applyStrategy, setApplyStrategy] = useState<'pass_rate' | 'best_candidate'>('pass_rate');
  const [minPassRate, setMinPassRate] = useState('1');
  const [sampleTags, setSampleTags] = useState<string[]>([]);
  const [sampleTagMatch, setSampleTagMatch] = useState<'any' | 'all'>('any');
  const [includedCallIds, setIncludedCallIds] = useState<string[]>(preselectedCallIds);
  const [excludedCallIds, setExcludedCallIds] = useState<string[]>([]);
  const [promptViews, setPromptViews] = useState<Record<string, PromptView>>({});
  const [expandedIterations, setExpandedIterations] = useState<Set<string>>(new Set());

  // Config list + registry pool scoped to the chosen agent kind.
  const configs = (targetType === 'quality' ? qualityConfigs : classifierConfigs) ?? [];
  const { data: registry } = useMisclassifiedCallList({ targetType });
  const allTags = [...new Set((registry ?? []).flatMap((r) => r.tags))].sort();

  // Sync form state from the loaded detail. Keyed on the run id so the sync
  // fires even when React Query serves cached data on mount (where `detail`
  // and `prevDetail` would start as the same reference and the old
  // `detail !== prevDetail` comparison would silently skip hydration).
  const [syncedId, setSyncedId] = useState<string | undefined>(undefined);
  if (detail && detail.id !== syncedId) {
    setSyncedId(detail.id);
    setName(detail.name);
    setTargetType(detail.target_type as FeedbackTargetType);
    setTargetConfigIds(detail.target_config_ids);
    setTargetAllUsed(detail.target_all_used_configs);
    setOptimizerModel(detail.optimizer_model);
    setMaxIterations(String(detail.max_iterations));
    setAutoUpdate(detail.auto_update);
    setApplyStrategy(detail.apply_strategy);
    setMinPassRate(String(detail.apply_min_pass_rate));
    setSampleTags(detail.sample_tags);
    setSampleTagMatch(detail.sample_tag_match);
    setIncludedCallIds(detail.included_call_ids);
    setExcludedCallIds(detail.excluded_call_ids);
  }

  const changeTargetType = (type: FeedbackTargetType) => {
    setTargetType(type);
    // Configs + registry differ per agent kind, so reset the selection.
    setTargetConfigIds([]);
    setTargetAllUsed(false);
    setSampleTags([]);
    setIncludedCallIds([]);
    setExcludedCallIds([]);
  };

  const toggleTargetConfig = (id: string) =>
    setTargetConfigIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );

  const toggleTag = (tag: string) =>
    setSampleTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));

  const matchesTags = (call: MisclassifiedCall) => {
    if (sampleTags.length === 0) return true;
    return sampleTagMatch === 'all'
      ? sampleTags.every((t) => call.tags.includes(t))
      : sampleTags.some((t) => call.tags.includes(t));
  };

  const rowStateOf = (call: MisclassifiedCall): RowState =>
    includedCallIds.includes(call.call_id)
      ? 'include'
      : excludedCallIds.includes(call.call_id)
        ? 'exclude'
        : 'auto';

  const isSelected = (call: MisclassifiedCall) => {
    if (excludedCallIds.includes(call.call_id)) return false;
    if (includedCallIds.includes(call.call_id)) return true;
    return matchesTags(call);
  };

  const setRowState = (callId: string, state: RowState) => {
    setIncludedCallIds((prev) => prev.filter((id) => id !== callId));
    setExcludedCallIds((prev) => prev.filter((id) => id !== callId));
    if (state === 'include') setIncludedCallIds((prev) => [...prev, callId]);
    if (state === 'exclude') setExcludedCallIds((prev) => [...prev, callId]);
  };

  const toggleIterationExpanded = (iterId: string) => {
    setExpandedIterations((prev) => {
      const next = new Set(prev);
      if (next.has(iterId)) next.delete(iterId);
      else next.add(iterId);
      return next;
    });
  };

  const selectedCalls = (registry ?? []).filter(isSelected);
  const selectedCount = selectedCalls.length;
  const configNameOf = (id: string) =>
    configs.find((c) => c.id === id)?.name ?? `${id.slice(0, 8)}…`;
  // With "target all used configs" the run derives its targets from the
  // selected calls — preview what that currently resolves to.
  const usedConfigIds = [
    ...new Set(selectedCalls.map((c) => c.target_config_id).filter((v): v is string => !!v)),
  ];
  const unlinkedSelectedCount = selectedCalls.filter((c) => !c.target_config_id).length;

  const buildPayload = () => ({
    name,
    target_type: targetType,
    target_config_ids: targetConfigIds,
    target_all_used_configs: targetAllUsed,
    optimizer_model: optimizerModel,
    max_iterations: Number(maxIterations) || 3,
    auto_update: autoUpdate,
    apply_strategy: applyStrategy,
    apply_min_pass_rate: Number(minPassRate),
    sample_tags: sampleTags,
    sample_tag_match: sampleTagMatch,
    included_call_ids: includedCallIds,
    excluded_call_ids: excludedCallIds,
  });

  const handleSave = async (): Promise<string | null> => {
    if (!name.trim()) {
      toast({ variant: 'destructive', title: 'Missing name', description: 'Add a run name.' });
      return null;
    }
    if (!targetAllUsed && targetConfigIds.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Missing config',
        description: 'Pick at least one target config, or target all used configs.',
      });
      return null;
    }
    try {
      if (isNew) {
        const created = await createMutation.mutateAsync(buildPayload());
        toast({
          variant: 'success',
          title: 'Created',
          description: `Run '${created.name}' created.`,
        });
        navigate(`/agent-improvement/judge-tuning/${created.id}`);
        return created.id;
      }
      await updateMutation.mutateAsync({ id: routeId, data: buildPayload() });
      toast({ variant: 'success', title: 'Saved', description: `Run '${name}' updated.` });
      return routeId;
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Save failed');
      toast({ variant: 'destructive', title: 'Save failed', description: msg });
      return null;
    }
  };

  const handleRun = async () => {
    const id = await handleSave();
    if (!id) return;
    try {
      await runMutation.mutateAsync(id);
      toast({
        variant: 'success',
        title: 'Run started',
        description: 'Optimizing — iterations will stream in.',
      });
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Run failed');
      toast({ variant: 'destructive', title: 'Run failed', description: msg });
    }
  };

  const handleApply = async (configId?: string) => {
    if (!routeId) return;
    try {
      await applyMutation.mutateAsync({ id: routeId, configId });
      toast({
        variant: 'success',
        title: 'Applied',
        description: configId
          ? `Candidate prompt written to '${configNameOf(configId)}'.`
          : 'Candidate prompts written to the target configs.',
      });
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Apply failed');
      toast({ variant: 'destructive', title: 'Apply failed', description: msg });
    }
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  const isBusy = createMutation.isPending || updateMutation.isPending;
  const isRunning = detail?.status === 'QUEUED' || detail?.status === 'RUNNING';
  const configEntries: [string, JudgeTuningConfigResult][] = Object.entries(
    detail?.config_results ?? {},
  );
  const unappliedConfigIds = configEntries
    .filter(([, r]) => r.candidatePrompt && !r.applied)
    .map(([id]) => id);

  /** Find the last iteration with structured edits for a config. */
  const lastEditsForConfig = (
    iters: JudgeTuningIteration[],
  ): { edits?: JudgeTuningPromptEdit[]; append?: string } | undefined => {
    for (let i = iters.length - 1; i >= 0; i--) {
      const e = extractEdits(iters[i].results);
      if (e) return e;
    }
    return undefined;
  };

  return (
    <div className="bg-muted/20 min-h-full">
      <div className="mx-auto max-w-5xl space-y-5 p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground -ml-2 h-8"
              onClick={() => navigate('/agent-improvement/judge-tuning')}
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Judge Tuning
            </Button>
            <h1 className="text-2xl font-semibold tracking-tight">
              {isNew ? 'New judge tuning run' : detail?.name}
            </h1>
            <p className="text-muted-foreground text-sm">
              Improve an agent&apos;s prompt from misclassified sample calls.
            </p>
          </div>
          {!isNew && detail && <StatusPill status={detail.status} />}
        </div>

        {/* 1 · Settings */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="bg-primary/10 text-primary flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold">
                1
              </span>
              Target &amp; rules
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Fix availability mislabels"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Agent</label>
                <div>
                  <Segmented<FeedbackTargetType>
                    value={targetType}
                    onChange={changeTargetType}
                    disabled={!isNew}
                    options={[
                      { value: 'classifier', label: 'Classifier' },
                      { value: 'quality', label: 'Quality' },
                    ]}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Target {targetType === 'quality' ? 'quality' : 'classifier'} configs
              </label>
              <label className="flex w-fit cursor-pointer items-center gap-2.5">
                <Checkbox
                  checked={targetAllUsed}
                  onCheckedChange={(v) => setTargetAllUsed(v === true)}
                />
                <span className="text-sm">Target all configs used by the selected calls</span>
              </label>
              {targetAllUsed ? (
                <div className="bg-muted/40 max-w-md space-y-1.5 rounded-lg px-3 py-2.5">
                  <p className="text-muted-foreground text-xs">
                    Targets are resolved when the run executes, from the configs the selected calls
                    are labeled with. Currently resolves to:
                  </p>
                  {usedConfigIds.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {usedConfigIds.map((id) => (
                        <Badge key={id} variant="secondary" className="text-xs">
                          {configNameOf(id)}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-amber-700">
                      None of the selected calls have a config set — set one on the registry
                      entries, or pick configs explicitly.
                    </p>
                  )}
                  {unlinkedSelectedCount > 0 && usedConfigIds.length > 0 && (
                    <p className="text-muted-foreground text-xs">
                      {unlinkedSelectedCount} selected call
                      {unlinkedSelectedCount === 1 ? ' has' : 's have'} no config set and will feed
                      every target.
                    </p>
                  )}
                </div>
              ) : (
                <div className="max-h-56 max-w-md divide-y overflow-y-auto rounded-lg border">
                  {configs.length === 0 && (
                    <p className="text-muted-foreground px-3 py-2.5 text-sm">No configs yet.</p>
                  )}
                  {configs.map((c) => (
                    <label
                      key={c.id}
                      className="hover:bg-muted/40 flex cursor-pointer items-center gap-2.5 px-3 py-2"
                    >
                      <Checkbox
                        checked={targetConfigIds.includes(c.id)}
                        onCheckedChange={() => toggleTargetConfig(c.id)}
                      />
                      <span className="text-sm">
                        {c.name}
                        {c.is_default ? ' (default)' : ''}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {!targetAllUsed && targetConfigIds.length > 1 && (
                <p className="text-muted-foreground text-xs">
                  Each config gets its own optimization run. Calls labeled with a config only feed
                  that config&apos;s run; unlabeled calls feed every run.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Optimizer model</label>
                <Input
                  value={optimizerModel}
                  onChange={(e) => setOptimizerModel(e.target.value)}
                  className="font-mono text-xs"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Max iterations</label>
                <Input
                  value={maxIterations}
                  onChange={(e) => setMaxIterations(e.target.value)}
                  type="number"
                  min="1"
                  max="20"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Apply strategy</label>
                <Select
                  value={applyStrategy}
                  onValueChange={(v) => setApplyStrategy(v as typeof applyStrategy)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pass_rate">Pass-rate threshold</SelectItem>
                    <SelectItem value="best_candidate">Best candidate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-muted/40 flex flex-wrap items-center gap-6 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2.5">
                <Switch checked={autoUpdate} onCheckedChange={setAutoUpdate} />
                <span className="text-sm font-medium">
                  {autoUpdate ? 'Auto-update config' : 'Manual apply only'}
                </span>
              </div>
              {applyStrategy === 'pass_rate' && (
                <div className="flex items-center gap-2">
                  <label className="text-muted-foreground text-sm">Min pass rate</label>
                  <Input
                    value={minPassRate}
                    onChange={(e) => setMinPassRate(e.target.value)}
                    type="number"
                    step="0.05"
                    min="0"
                    max="1"
                    className="w-24"
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 2 · Sample selection */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="bg-primary/10 text-primary flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold">
                2
              </span>
              Sample selection
              <span className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                {selectedCount} selected
              </span>
            </CardTitle>
            <p className="text-muted-foreground pl-8 text-sm">
              Choose calls by tag, then fine-tune any single call with the override.
            </p>
            {preselectedCallIds.length > 0 && (
              <p className="pl-8 text-xs text-blue-600">
                {preselectedCallIds.length} call{preselectedCallIds.length === 1 ? '' : 's'}{' '}
                preselected from run
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">Tags</span>
              {allTags.length === 0 && (
                <span className="text-muted-foreground text-xs">
                  No tags yet — add calls in Misclassified Calls first.
                </span>
              )}
              {allTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    sampleTags.includes(tag)
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-input hover:bg-muted',
                  )}
                >
                  {tag}
                </button>
              ))}
              {sampleTags.length > 1 && (
                <div className="ml-1 flex items-center gap-2">
                  <span className="text-muted-foreground text-xs">match</span>
                  <Select
                    value={sampleTagMatch}
                    onValueChange={(v) => setSampleTagMatch(v as 'any' | 'all')}
                  >
                    <SelectTrigger className="h-7 w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">any</SelectItem>
                      <SelectItem value="all">all</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {(registry ?? []).length === 0 ? (
              <div className="text-muted-foreground rounded-lg border border-dashed py-8 text-center text-sm">
                No {targetType} calls in the registry yet.
              </div>
            ) : (
              <div className="divide-y rounded-lg border">
                {(registry ?? []).map((c) => {
                  const state = rowStateOf(c);
                  const selected = isSelected(c);
                  return (
                    <div
                      key={c.id}
                      className={cn(
                        'flex items-center justify-between gap-4 px-3 py-2.5',
                        selected ? 'bg-emerald-50/40' : 'hover:bg-muted/40',
                      )}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        {selected ? (
                          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                        ) : (
                          <span className="border-muted-foreground/30 h-4 w-4 shrink-0 rounded-full border" />
                        )}
                        <span className="font-mono text-xs">{c.call_id.slice(0, 8)}…</span>
                        <div className="hidden flex-wrap gap-1 sm:flex">
                          {c.tags.map((t) => (
                            <Badge key={t} variant="secondary" className="text-xs">
                              {t}
                            </Badge>
                          ))}
                          {Object.keys(c.expected_labels ?? {}).length === 0 &&
                            Object.keys(c.expected_data ?? {}).length === 0 && (
                              <Badge className="bg-amber-100 text-xs text-amber-800 hover:bg-amber-100">
                                no expected values
                              </Badge>
                            )}
                        </div>
                      </div>
                      <Segmented<RowState>
                        size="sm"
                        value={state}
                        onChange={(s) => setRowState(c.call_id, s)}
                        options={[
                          { value: 'auto', label: 'Auto' },
                          {
                            value: 'include',
                            label: 'Include',
                            activeClass: 'bg-emerald-500 text-white shadow-sm',
                          },
                          {
                            value: 'exclude',
                            label: 'Exclude',
                            activeClass: 'bg-rose-500 text-white shadow-sm',
                          },
                        ]}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Run results */}
        {!isNew && detail && (
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  Run results <StatusPill status={detail.status} />
                </CardTitle>
                <div className="flex items-center gap-2">
                  {unappliedConfigIds.length > 1 && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleApply()}
                      disabled={applyMutation.isPending || isRunning}
                    >
                      <Wand2 className="mr-1.5 h-3.5 w-3.5" /> Apply all
                    </Button>
                  )}
                  {isRunning && <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />}
                </div>
              </div>
              <p className="text-muted-foreground text-sm">
                {detail.result_summary?.baselinePassRate !== undefined
                  ? `Baseline ${pct(detail.result_summary.baselinePassRate)} → best ${pct(detail.result_summary.bestPassRate)}${
                      (detail.result_summary.configCount ?? 1) > 1
                        ? ` across ${detail.result_summary.configCount} configs`
                        : ''
                    }${detail.result_summary.applied ? ' · applied' : ''}`
                  : 'Not run yet — press Save & Run below.'}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Progress card while running */}
              {isRunning && detail.progress && (
                <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50/50 p-4 dark:border-blue-800 dark:bg-blue-950/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
                      <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                        {STAGE_LABEL[detail.progress.stage] ?? detail.progress.stage}
                      </span>
                      {detail.progress.configName && (
                        <Badge variant="secondary" className="text-xs">
                          {detail.progress.configName}
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-blue-600 dark:text-blue-400">
                      {timeAgo(detail.progress.updatedAt)}
                    </span>
                  </div>
                  {detail.progress.current != null &&
                    detail.progress.total != null &&
                    detail.progress.total > 0 && (
                      <Progress
                        value={Math.round((detail.progress.current / detail.progress.total) * 100)}
                        className="h-2"
                      />
                    )}
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    {detail.progress.message}
                  </p>
                </div>
              )}

              {detail.error && (
                <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {detail.error}
                </p>
              )}

              {configEntries.map(([configId, r]) => {
                const iters = detail.iterations.filter((it) => it.target_config_id === configId);
                const lastEdits = lastEditsForConfig(iters);
                return (
                  <div key={configId} className="space-y-3 rounded-lg border p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{configNameOf(configId)}</span>
                        <StatusPill status={r.status} />
                        {r.applied && (
                          <Badge variant="secondary" className="text-xs">
                            applied
                          </Badge>
                        )}
                      </div>
                      {r.status !== 'SKIPPED' && (
                        <span className="text-muted-foreground text-xs">
                          {pct(r.baselinePassRate)} → {pct(r.bestPassRate)} (iteration{' '}
                          {r.bestIteration ?? 0}) · {r.totalSamples ?? 0} call
                          {(r.totalSamples ?? 0) === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>

                    {r.status === 'SKIPPED' ? (
                      <p className="text-muted-foreground text-sm">{r.error ?? 'Skipped.'}</p>
                    ) : (
                      <>
                        {iters.length > 0 && (
                          <div className="space-y-2">
                            {iters.map((it) => {
                              const full = it.passed_count === it.total_count && it.total_count > 0;
                              const ec = editCount(it.results);
                              const isExpanded = expandedIterations.has(it.id);
                              const iterEdits = extractEdits(it.results);
                              return (
                                <div key={it.id} className="rounded-lg border p-3 text-sm">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">
                                        {it.iteration === 0
                                          ? 'Baseline'
                                          : `Iteration ${it.iteration}`}
                                      </span>
                                      {ec > 0 && (
                                        <button
                                          type="button"
                                          onClick={() => toggleIterationExpanded(it.id)}
                                          className="flex items-center gap-0.5 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700 hover:bg-blue-200"
                                        >
                                          {ec} edit{ec === 1 ? '' : 's'}
                                          {isExpanded ? (
                                            <ChevronDown className="h-3 w-3" />
                                          ) : (
                                            <ChevronRight className="h-3 w-3" />
                                          )}
                                        </button>
                                      )}
                                    </div>
                                    <span
                                      className={cn(
                                        'rounded-full px-2 py-0.5 text-xs font-semibold',
                                        full
                                          ? 'bg-emerald-100 text-emerald-800'
                                          : 'bg-amber-100 text-amber-800',
                                      )}
                                    >
                                      {it.passed_count}/{it.total_count} passed
                                    </span>
                                  </div>
                                  {it.rationale && (
                                    <p className="text-muted-foreground mt-1 text-xs">
                                      {it.rationale}
                                    </p>
                                  )}
                                  {isExpanded && iterEdits && (
                                    <div className="mt-2">
                                      <EditList
                                        edits={iterEdits.edits ?? []}
                                        append={iterEdits.append}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {!r.candidatePrompt && r.status === 'SUCCEEDED' && (
                          <p className="text-muted-foreground text-sm">
                            The current prompt already passed every sample — nothing to improve.
                          </p>
                        )}

                        {r.candidatePrompt && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Segmented<PromptView>
                                size="sm"
                                value={promptViews[configId] ?? 'changes'}
                                onChange={(v) =>
                                  setPromptViews((prev) => ({ ...prev, [configId]: v }))
                                }
                                options={[
                                  { value: 'changes', label: 'Changes' },
                                  { value: 'full', label: 'Full prompts' },
                                ]}
                              />
                            </div>

                            {(promptViews[configId] ?? 'changes') === 'changes' ? (
                              <PromptDiff
                                before={r.basePrompt ?? ''}
                                after={r.candidatePrompt}
                                edits={lastEdits?.edits}
                                append={lastEdits?.append}
                              />
                            ) : (
                              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                <div className="space-y-1">
                                  <label className="text-muted-foreground text-xs font-medium">
                                    Current prompt
                                  </label>
                                  <textarea
                                    readOnly
                                    value={r.basePrompt ?? ''}
                                    rows={14}
                                    className="border-input bg-muted w-full rounded-md border px-3 py-2 font-mono text-xs"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-xs font-medium text-emerald-700">
                                    Improved prompt
                                  </label>
                                  <textarea
                                    readOnly
                                    value={r.candidatePrompt}
                                    rows={14}
                                    className="w-full rounded-md border border-emerald-200 bg-emerald-50/30 px-3 py-2 font-mono text-xs"
                                  />
                                </div>
                              </div>
                            )}

                            <div className="flex justify-end">
                              <Button
                                onClick={() => handleApply(configId)}
                                disabled={applyMutation.isPending || r.applied}
                              >
                                <Wand2 className="mr-2 h-4 w-4" />
                                {r.applied ? 'Applied' : 'Apply to config'}
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Action bar */}
        <div className="bg-background sticky bottom-4 flex items-center justify-between gap-4 rounded-xl border px-4 py-3 shadow-lg">
          <div className="text-muted-foreground text-xs">
            {selectedCount} call{selectedCount === 1 ? '' : 's'} selected
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate('/agent-improvement/judge-tuning')}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={handleSave} disabled={isBusy}>
              <Save className="mr-2 h-4 w-4" /> {isBusy ? 'Saving…' : 'Save'}
            </Button>
            <Button onClick={handleRun} disabled={isBusy || isRunning}>
              <Play className="mr-2 h-4 w-4" /> {isRunning ? 'Running…' : 'Save & Run'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
